import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';

import { IconButton } from '@mui/material';

import { ReactComponent as DragIcon } from '../../../../../../assets/icons/v2/drag.svg';

import {
  fieldName,
  ingredientField,
} from '../../formFields';

import { AllergenTag, AllergenTagText } from '../../../../../utils/meals';
import { AttentionTag, LightTag } from '../../../../../../components/Tags';
import { StyledTrashIcon } from '../../styles';

import {
  IngredientContainer,
  HeaderContainer,
  IngredientHeader,
  StyledTypography,
  AllergensLabel,
  StyledFilter,
  SmallFormInput,
  BigFormInput,
  FieldsContainer,
  AllergensContainer,
  AllergensSubContainer,
  AllergenTagContainer,
  AutocompleteContainer,
  CanonicalMetadataStrip,
  InlineHint,
} from './styles';

import IngredientAutocomplete from './IngredientAutocomplete';

import texts from './texts';

import configs from './configs';

import { formatMacro, macroPillText } from './utils';

const allergenValues = Object.values(AllergenTag).map((tag) => ({
  value: tag,
  label: AllergenTagText[tag],
}));

const IngredientsRow = ({
  index,
  removeRow,
  canonicalIngredients,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const row = values[fieldName.INGREDIENTS][index];
  const canonicalId = row[ingredientField.CANONICAL_INGREDIENT_ID];
  const ingredientName = row[ingredientField.NAME];

  const [allergenOptions, setAllergenOptions] = useState(
    row[ingredientField.ALLERGEN_TAGS],
  );

  const canonicalMeta = useMemo(() => (
    canonicalId
      ? canonicalIngredients.find((item) => item.id === canonicalId) || null
      : null
  ), [canonicalId, canonicalIngredients]);

  const allergenRef = useRef(null);

  const fieldBaseName = `${fieldName.INGREDIENTS}.${index}`;

  const memoizedTags = useMemo(() => allergenOptions.map((option) => (
    <AttentionTag key={option}>
      {AllergenTagText[AllergenTag[option]]}
    </AttentionTag>
  )), [allergenOptions]);

  const handleAllergenChange = useCallback((options) => {
    setAllergenOptions(options);
  }, []);

  const handleAutocompleteChange = useCallback(({
    name,
    id,
    allergenTags,
  }) => {
    setFieldValue(`${fieldBaseName}.${ingredientField.NAME}`, name || '');
    setFieldValue(`${fieldBaseName}.${ingredientField.CANONICAL_INGREDIENT_ID}`, id || null);
    if (id && Array.isArray(allergenTags)) {
      setFieldValue(`${fieldBaseName}.${ingredientField.ALLERGEN_TAGS}`, allergenTags);
      setAllergenOptions(allergenTags);
      allergenRef.current = allergenTags;
    } else if (!id && canonicalId) {
      setFieldValue(`${fieldBaseName}.${ingredientField.ALLERGEN_TAGS}`, []);
      setAllergenOptions([]);
      allergenRef.current = [];
    }
  }, [fieldBaseName, setFieldValue, canonicalId]);

  useEffect(() => {
    const prevAllergenOptions = allergenRef?.current;

    if (prevAllergenOptions && (allergenOptions.length !== prevAllergenOptions.length)) {
      // selected allergens have been changed for the ingredient
      const currentTags = values[fieldName.INGREDIENTS][index][ingredientField.ALLERGEN_TAGS];
      let updatedValues = null;

      if (allergenOptions.length > prevAllergenOptions.length) {
        // new allergens have been selected. add them to values
        const addedOptions = allergenOptions.filter((option) => !prevAllergenOptions.includes(option));
        updatedValues = [...currentTags, ...addedOptions];
      } else if (allergenOptions.length < prevAllergenOptions.length) {
        // allergens have been unselected. remove the changes from values
        const removedOptions = prevAllergenOptions.filter((option) => !allergenOptions.includes(option));
        updatedValues = currentTags.filter((item) => !removedOptions.includes(item));
      }

      setFieldValue(`${fieldBaseName}.${ingredientField.ALLERGEN_TAGS}`, updatedValues);
    }

    allergenRef.current = allergenOptions;
  }, [
    allergenOptions,
    index,
    values,
    setFieldValue,
    fieldBaseName,
  ]);

  return (
    <IngredientContainer>
      <HeaderContainer>
        <IconButton className="drag-handle">
          <DragIcon />
        </IconButton>
        <IngredientHeader>{texts.addIngredient}</IngredientHeader>
        <IconButton onClick={removeRow}>
          <StyledTrashIcon />
        </IconButton>
      </HeaderContainer>
      <FieldsContainer>
        <AutocompleteContainer>
          <IngredientAutocomplete
            name={ingredientName}
            canonicalIngredient={canonicalMeta}
            canonicalIngredients={canonicalIngredients}
            onChange={handleAutocompleteChange}
            label={`${texts.field[ingredientField.NAME].label}:`}
          />
        </AutocompleteContainer>
        <SmallFormInput
          name={`${fieldBaseName}.${ingredientField.AMOUNT}`}
          label={`${texts.field[ingredientField.AMOUNT].label}:`}
        />
        <SmallFormInput
          name={`${fieldBaseName}.${ingredientField.UNIT}`}
          label={`${texts.field[ingredientField.UNIT].label}:`}
        />
        <StyledTypography>(</StyledTypography>
        <SmallFormInput
          name={`${fieldBaseName}.${ingredientField.SECOND_AMOUNT}`}
          label={`${texts.field[ingredientField.AMOUNT].label}:`}
        />
        <SmallFormInput
          name={`${fieldBaseName}.${ingredientField.SECOND_UNIT}`}
          label={`${texts.field[ingredientField.UNIT].label}:`}
        />
        <StyledTypography>)</StyledTypography>
        <BigFormInput
          name={`${fieldBaseName}.${ingredientField.PREPARATION_COMMENT}`}
          label={`${texts.field[ingredientField.PREPARATION_COMMENT].label}:`}
        />
      </FieldsContainer>
      {!!canonicalMeta && (
        <CanonicalMetadataStrip>
          <LightTag>{`${formatMacro(canonicalMeta.caloriesPer100g)} ${texts.autocomplete.caloriesLabel}`}</LightTag>
          <LightTag>{macroPillText(texts.autocomplete.proteinLabel, canonicalMeta.proteinPer100g)}</LightTag>
          <LightTag>{macroPillText(texts.autocomplete.carbsLabel, canonicalMeta.carbsPer100g)}</LightTag>
          <LightTag>{macroPillText(texts.autocomplete.fatLabel, canonicalMeta.fatPer100g)}</LightTag>
        </CanonicalMetadataStrip>
      )}
      <AllergensContainer>
        <AllergensLabel>{`${texts.allergensTitle}:`}</AllergensLabel>
        <AllergensSubContainer>
          {canonicalId && allergenOptions.length === 0 && (
            <InlineHint>{texts.allergensNoneLockedHint}</InlineHint>
          )}
          {canonicalId && allergenOptions.length > 0 && (
            <>
              <InlineHint>{texts.allergensLockedHint}</InlineHint>
              <AllergenTagContainer>{memoizedTags}</AllergenTagContainer>
            </>
          )}
          {!canonicalId && (
            <>
              <StyledFilter
                options={allergenValues}
                initialValues={allergenOptions}
                onValuesSelected={handleAllergenChange}
                className={configs.classNames.filter}
                filterIcon={null}
                disableValueRender
              />
              <AllergenTagContainer>
                {memoizedTags.length > 0 ? memoizedTags : (
                  <InlineHint>{texts.allergensNoneHint}</InlineHint>
                )}
              </AllergenTagContainer>
            </>
          )}
        </AllergensSubContainer>
      </AllergensContainer>
    </IngredientContainer>
  );
};

IngredientsRow.propTypes = {
  index: PropTypes.number.isRequired,
  removeRow: PropTypes.func.isRequired,
  canonicalIngredients: PropTypes.arrayOf(PropTypes.object),
};

IngredientsRow.defaultProps = {
  canonicalIngredients: [],
};

export default IngredientsRow;
